import React, { useEffect, useState } from 'react'
import type { CancelarPolizaRequest, PolizaDTO, RazonDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { currencyCode, errorMessage, formatCompactDate, money, personName, statusKey } from '../common/policy-common'

export interface PolicyCloseProps {
    polizaId: number | null
    onClose: () => void
    onCompleted?: (poliza?: PolizaDTO | null) => void
}

export interface PolicyCloseForm {
    razon: string
    observaciones: string
}

export const AVISO = 'Se detendrán los avisos automáticos de renovación y cobranza para esta póliza.'
export const MIN_COMENTARIO = 10
export const MAX_COMENTARIO = 500

const LOAD_ERROR = 'No se pudo cargar el formulario.'
const SAVE_ERROR = 'No se pudo cerrar la póliza.'

const emptyForm = (): PolicyCloseForm => ({
    razon: '',
    observaciones: '',
})

export const usePolicyCloseModel = ({ polizaId, onCompleted }: PolicyCloseProps) => {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [tried, setTried] = useState(false)
    const [done, setDone] = useState(false)
    const [razones, setRazones] = useState<RazonDTO[]>([])
    const [poliza, setPoliza] = useState<PolizaDTO | null>(null)
    const [algunoPagado, setAlgunoPagado] = useState(false)
    const [form, setForm] = useState<PolicyCloseForm>(emptyForm)

    useEffect(() => {
        if (!polizaId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError('')
                setTried(false)
                setDone(false)
                setForm(emptyForm())
                const [razonesResponse, polizaResponse] = await Promise.all([
                    APIInvoke.invokeGET('/razones?modelo=0&tipo=0'),
                    APIInvoke.invokeGET(`/polizas/${polizaId}`),
                ])
                const razonesPayload: WrapperResponse<RazonDTO[]> | null = await razonesResponse.json()
                const polizaPayload: WrapperResponse<PolizaDTO> | null = await polizaResponse.json()
                if (!active) return
                if (!razonesPayload?.ok) throw new Error(razonesPayload?.message || 'No se pudieron cargar las razones.')
                if (!polizaPayload?.ok || !polizaPayload.body) throw new Error(polizaPayload?.message || 'No se pudo cargar la póliza.')
                const body = polizaPayload.body
                setRazones(razonesPayload.body || [])
                setPoliza(body)
                setAlgunoPagado((body.recibos || []).some((item) => statusKey(item.status) === 'pagado'))
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [polizaId])

    const patch = (next: Partial<PolicyCloseForm>) => setForm((current) => ({ ...current, ...next }))
    const comentarioCorto = form.observaciones.trim().length < MIN_COMENTARIO

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setTried(true)
        setError('')
        if (!polizaId || !form.razon || comentarioCorto) return

        try {
            setSaving(true)
            const request: CancelarPolizaRequest = {
                razonId: Number(form.razon),
                texto: form.observaciones.trim(),
            }
            const response = await APIInvoke.invokePUT(`/polizas/${polizaId}/cancelar`, request)
            const payload: WrapperResponse<PolizaDTO> | null = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || SAVE_ERROR)
            setDone(true)
            onCompleted?.(payload.body)
        } catch (submitError) {
            setError(errorMessage(submitError, SAVE_ERROR))
        } finally {
            setSaving(false)
        }
    }

    const fin = formatCompactDate(poliza?.fechaVigenciaA)

    return {
        loading,
        saving,
        error,
        done,
        razones,
        poliza,
        form,
        contratante: poliza?.cliente ? personName(poliza.cliente) : '',
        fin,
        prima: poliza ? money(poliza.prima, currencyCode(poliza.moneda)) : null,
        title: algunoPagado ? 'Cerrar póliza' : 'Cancelar póliza',
        submitLabel: algunoPagado ? 'CERRAR PÓLIZA' : 'CANCELAR PÓLIZA',
        razonError: tried && !form.razon,
        comentarioError: tried && comentarioCorto,
        razonNombre: razones.find((item) => String(item.id) === form.razon)?.nombre || '',
        changeRazon: (value: string | number) => patch({ razon: String(value) }),
        changeObservaciones: (value: string) => patch({ observaciones: value.slice(0, MAX_COMENTARIO) }),
        submit,
    }
}
